import { useState, useEffect } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../../context/AppContext.jsx';
import { toDisplayUnits } from '../../services/WaypointService.js';

export default function FeatureEditor() {
  const { currentFeatures, setCurrentFeatures } = useFile();
  const { selectedFeatureIndex, setSelectedFeatureIndex } = useSelection();
  const { visualizer } = useVisualizer();
  const { showStatus } = useUI();
  const [formData, setFormData] = useState({
    name: '',
    posX: 0, posY: 0, posZ: 0,
    rotRX: 0, rotRY: 0, rotRZ: 0,
  });

  useEffect(() => {
    if (selectedFeatureIndex !== null && currentFeatures[selectedFeatureIndex]) {
      const feature = currentFeatures[selectedFeatureIndex];
      const displayUnits = toDisplayUnits(feature.position, feature.rotation);
      setFormData({
        name: feature.name || '',
        ...displayUnits,
      });
    }
  }, [selectedFeatureIndex, currentFeatures]);

  const handleUpdate = () => {
    if (selectedFeatureIndex === null) return;

    const updatedFeatures = [...currentFeatures];
    const feature = updatedFeatures[selectedFeatureIndex];

    // Update feature with new values
    feature.name = formData.name;
    feature.position.x = parseFloat(formData.posX) / 1000;
    feature.position.y = parseFloat(formData.posY) / 1000;
    feature.position.z = parseFloat(formData.posZ) / 1000;
    feature.rotation.rx = parseFloat(formData.rotRX) * (Math.PI / 180);
    feature.rotation.ry = parseFloat(formData.rotRY) * (Math.PI / 180);
    feature.rotation.rz = parseFloat(formData.rotRZ) * (Math.PI / 180);

    setCurrentFeatures(updatedFeatures);

    // Update visualizer
    if (visualizer) {
      visualizer.visualizeFeatures(updatedFeatures);
    }

    showStatus('Feature updated successfully', 'success');
  };

  const handleDelete = () => {
    if (selectedFeatureIndex === null) return;
    if (!confirm(`Are you sure you want to delete feature "${currentFeatures[selectedFeatureIndex].name}"?`)) return;

    const updatedFeatures = currentFeatures.filter((_, index) => index !== selectedFeatureIndex);
    setCurrentFeatures(updatedFeatures);

    // Update visualizer
    if (visualizer) {
      visualizer.visualizeFeatures(updatedFeatures);
    }

    setSelectedFeatureIndex(null);
    showStatus('Feature deleted successfully', 'success');
  };

  const handleCancel = () => {
    setSelectedFeatureIndex(null);
  };

  if (selectedFeatureIndex === null || !currentFeatures[selectedFeatureIndex]) return null;

  const feature = currentFeatures[selectedFeatureIndex];

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-border flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span>Edit Feature</span>
      </h2>

      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium mb-2">Feature Name</h4>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-bg-tertiary border border-border rounded px-2 py-1.5 text-text-primary text-sm font-mono"
            placeholder="Feature name"
          />
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Position (millimeters)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">X:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posX}
              onChange={(e) => setFormData({ ...formData, posX: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">Y:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posY}
              onChange={(e) => setFormData({ ...formData, posY: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">Z:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posZ}
              onChange={(e) => setFormData({ ...formData, posZ: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Rotation (degrees)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RX:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRX}
              onChange={(e) => setFormData({ ...formData, rotRX: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RY:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRY}
              onChange={(e) => setFormData({ ...formData, rotRY: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RZ:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRZ}
              onChange={(e) => setFormData({ ...formData, rotRZ: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleUpdate}
            className="flex-1 bg-primary text-white px-3 py-1.5 text-sm rounded hover:bg-blue-600 transition-colors"
          >
            Update Feature
          </button>
          <button
            onClick={handleDelete}
            className="flex-1 bg-error text-white px-3 py-1.5 text-sm rounded hover:bg-red-600 transition-colors"
          >
            Delete Feature
          </button>
        </div>
      </div>
    </section>
  );
}


